import { defineStore } from 'pinia';
import axios from '@/api/config/axios';

export const useNotificacoesStore = defineStore('notificacoes', {
    state: () => ({
        notifications: [],
        unreadCount: 0,
        loading: false,
        error: null,
        pollingInterval: null,
        isPolling: false,
        echoChannel: null,
        echoChannelName: null,
        isListening: false,
        _audioContext: null, // Contexto de áudio singleton para reutilização
        _audioContextUnlocked: false // Flag para indicar se o contexto foi "destravado" por interação do usuário
    }),

    getters: {
        getNotifications: (state) => state.notifications,
        getUnreadCount: (state) => state.unreadCount,
        getLoading: (state) => state.loading,
        getError: (state) => state.error,
        hasUnread: (state) => state.unreadCount > 0,
        unreadNotifications: (state) => state.notifications.filter(n => !n.read_at),
        readNotifications: (state) => state.notifications.filter(n => n.read_at)
    },

    actions: {
        /**
         * Busca as notificações da API
         */
        async fetchNotifications() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get('/api/v1/notifications');
                
                console.log('Response notifications:', response.data);
                
                // Trata diferentes formatos de resposta
                if (response.data) {
                    // Formato 1: { notifications: [], unread_count: 5 }
                    if (response.data.notifications) {
                        this.notifications = response.data.notifications;
                        this.unreadCount = response.data.unread_count || 0;
                    }
                    // Formato 2: { data: { notifications: [], unread_count: 5 } }
                    else if (response.data.data && response.data.data.notifications) {
                        this.notifications = response.data.data.notifications;
                        this.unreadCount = response.data.data.unread_count || 0;
                    }
                    else {
                        this.notifications = [];
                        this.unreadCount = 0;
                    }
                } else {
                    this.notifications = [];
                    this.unreadCount = 0;
                }
                
                console.log('Notificações carregadas:', this.notifications.length);
                console.log('Contagem não lidas:', this.unreadCount);
                
                return response.data;
            } catch (error) {
                this.error = 'Erro ao buscar notificações';
                console.error('Erro ao buscar notificações:', error);
                console.error('Detalhes do erro:', error.response?.data || error.message);
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Busca apenas a contagem de notificações não lidas (endpoint otimizado)
         */
        async fetchUnreadCount() {
            try {
                console.log('🔄 fetchUnreadCount: Fazendo requisição para /api/v1/notifications/unread-count');
                const response = await axios.get('/api/v1/notifications/unread-count');
                
                // Log para debug
                console.log('✅ fetchUnreadCount: Response recebido:', response.data);
                
                // A resposta pode vir em diferentes formatos, vamos tratar todos
                let count = 0;
                
                if (response.data !== undefined && response.data !== null) {
                    // Formato 1: Número direto
                    if (typeof response.data === 'number') {
                        count = response.data;
                    }
                    // Formato 2: Objeto com data.data
                    else if (response.data.data !== undefined) {
                        if (typeof response.data.data === 'number') {
                            count = response.data.data;
                        } else if (response.data.data.unread_count !== undefined) {
                            count = response.data.data.unread_count;
                        } else if (response.data.data.count !== undefined) {
                            count = response.data.data.count;
                        }
                    }
                    // Formato 3: Objeto direto
                    else if (response.data.unread_count !== undefined) {
                        count = response.data.unread_count;
                    } else if (response.data.count !== undefined) {
                        count = response.data.count;
                    }
                }
                
                // Atualiza apenas se conseguiu extrair o valor
                if (count !== undefined && count !== null) {
                    this.unreadCount = count;
                    console.log('Contagem atualizada:', this.unreadCount);
                } else {
                    console.warn('Não foi possível extrair a contagem da resposta:', response.data);
                }
                
                return this.unreadCount;
            } catch (error) {
                console.error('Erro ao buscar contagem de notificações não lidas:', error);
                console.error('Detalhes do erro:', error.response?.data || error.message);
                // Não lança erro para não interromper o polling, apenas loga
                return this.unreadCount;
            }
        },

        /**
         * Marca uma notificação como lida
         * @param {string} notificationId - ID da notificação
         */
        async markAsRead(notificationId) {
            try {
                const response = await axios.post(`/api/v1/notifications/${notificationId}/read`);
                
                // Atualiza a notificação localmente
                const notification = this.notifications.find(n => n.id === notificationId);
                if (notification) {
                    notification.read_at = new Date().toISOString();
                }
                
                // Atualiza o contador de não lidas (localmente e depois busca do servidor para garantir sincronização)
                if (this.unreadCount > 0) {
                    this.unreadCount--;
                }
                
                // Sincroniza a contagem com o servidor
                await this.fetchUnreadCount();
                
                return response.data;
            } catch (error) {
                this.error = 'Erro ao marcar notificação como lida';
                console.error('Erro ao marcar notificação como lida:', error);
                throw error;
            }
        },

        /**
         * Marca todas as notificações como lidas
         */
        async markAllAsRead() {
            try {
                const response = await axios.post('/api/v1/notifications/read-all');
                
                // Atualiza todas as notificações localmente
                this.notifications.forEach(notification => {
                    if (!notification.read_at) {
                        notification.read_at = new Date().toISOString();
                    }
                });
                
                this.unreadCount = 0;
                
                // Sincroniza a contagem com o servidor
                await this.fetchUnreadCount();
                
                return response.data;
            } catch (error) {
                this.error = 'Erro ao marcar todas as notificações como lidas';
                console.error('Erro ao marcar todas as notificações como lidas:', error);
                throw error;
            }
        },

        /**
         * Remove uma notificação
         * @param {string} notificationId - ID da notificação
         */
        async deleteNotification(notificationId) {
            try {
                const response = await axios.delete(`/api/v1/notifications/${notificationId}`);
                
                // Remove a notificação da lista local
                const index = this.notifications.findIndex(n => n.id === notificationId);
                if (index !== -1) {
                    const notification = this.notifications[index];
                    // Se a notificação não estava lida, decrementa o contador
                    if (!notification.read_at && this.unreadCount > 0) {
                        this.unreadCount--;
                    }
                    this.notifications.splice(index, 1);
                }
                
                // Sincroniza a contagem com o servidor
                await this.fetchUnreadCount();
                
                return response.data;
            } catch (error) {
                this.error = 'Erro ao remover notificação';
                console.error('Erro ao remover notificação:', error);
                throw error;
            }
        },

        /**
         * Inicia o polling automático de notificações
         * Usa o endpoint otimizado de contagem para reduzir o tráfego de rede
         * @param {number} interval - Intervalo em milissegundos (padrão: 30000 = 30s)
         */
        startPolling(interval = 30000) {
            if (this.isPolling) {
                console.log('⚠️ startPolling: Polling já está ativo. Ignorando chamada duplicada.');
                return; // Já está fazendo polling
            }

            console.log('🚀 startPolling: Iniciando polling de notificações com intervalo de', interval, 'ms');
            this.isPolling = true;
            
            // Busca a contagem imediatamente (mais leve que buscar todas as notificações)
            console.log('📞 startPolling: Fazendo primeira chamada de fetchUnreadCount...');
            this.fetchUnreadCount().then(() => {
                console.log('✅ startPolling: Primeira chamada concluída com sucesso');
            }).catch(err => {
                console.error('❌ startPolling: Erro na primeira chamada:', err);
            });
            
            // Configura o intervalo para buscar apenas a contagem (polling otimizado)
            this.pollingInterval = setInterval(() => {
                console.log('⏰ Polling (interval): buscando contagem...');
                this.fetchUnreadCount();
            }, interval);
            
            console.log('✅ startPolling: Polling iniciado com sucesso. Interval ID:', this.pollingInterval);
            console.log('📊 startPolling: Próxima atualização em', interval / 1000, 'segundos');
        },

        /**
         * Para o polling automático de notificações
         */
        stopPolling() {
            if (this.pollingInterval) {
                console.log('🛑 stopPolling: Parando polling. Interval ID:', this.pollingInterval);
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
                this.isPolling = false;
                console.log('✅ stopPolling: Polling parado com sucesso');
            } else {
                console.log('ℹ️ stopPolling: Nenhum polling ativo para parar');
            }
        },

        /**
         * "Destrava" o contexto de áudio após primeira interação do usuário
         * Necessário devido às políticas de autoplay dos navegadores modernos
         */
        unlockAudioContext() {
            if (this._audioContextUnlocked) {
                return; // Já foi destravado
            }

            try {
                if (!this._audioContext) {
                    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                    if (AudioContextClass) {
                        this._audioContext = new AudioContextClass();
                    }
                }

                if (this._audioContext && this._audioContext.state === 'suspended') {
                    this._audioContext.resume().then(() => {
                        this._audioContextUnlocked = true;
                        console.log('🔓 Contexto de áudio destravado com sucesso');
                    }).catch(err => {
                        console.warn('⚠️ Não foi possível destravar o contexto de áudio:', err);
                    });
                } else if (this._audioContext && this._audioContext.state === 'running') {
                    this._audioContextUnlocked = true;
                    console.log('✅ Contexto de áudio já está em execução');
                }
            } catch (error) {
                console.warn('⚠️ Erro ao destravar contexto de áudio:', error);
            }
        },

        /**
         * Toca um som de notificação suave e discreto
         * Usa Web Audio API com tratamento adequado de autenticação de usuário
         */
        async playNotificationSound() {
            console.log('🎵 playNotificationSound: Iniciando reprodução do som...');
            
            // Verifica se o navegador suporta Web Audio API
            if (!window.AudioContext && !window.webkitAudioContext) {
                console.warn('⚠️ Web Audio API não suportada neste navegador');
                return;
            }

            try {
                // Usa um contexto de áudio singleton ou cria novo
                let audioContext = this._audioContext;
                if (!audioContext || audioContext.state === 'closed') {
                    console.log('🔧 Criando novo contexto de áudio...');
                    audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    this._audioContext = audioContext;
                }

                console.log('🔍 Estado do contexto de áudio:', audioContext.state);

                // Se o contexto estiver suspenso, tenta resumir
                if (audioContext.state === 'suspended') {
                    console.log('⏸️ Contexto suspenso. Tentando retomar...');
                    try {
                        await audioContext.resume();
                        this._audioContextUnlocked = true;
                        console.log('✅ Contexto de áudio retomado com sucesso');
                    } catch (error) {
                        console.warn('⚠️ Não foi possível retomar o contexto de áudio:', error);
                        console.warn('💡 Dica: O usuário precisa interagir com a página primeiro para destravar o áudio');
                        return;
                    }
                } else if (audioContext.state === 'running') {
                    this._audioContextUnlocked = true;
                }

                // Cria os nós de áudio
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                // Conecta os nós
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                // Configura um tom suave tipo "ping" (frequências ascendentes)
                oscillator.type = 'sine';
                
                // Primeira nota (mais grave)
                oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
                // Segunda nota (média)
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.08);
                // Terceira nota (mais aguda)
                oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.15);

                // Configura envelope de volume (attack-decay)
                gainNode.gain.setValueAtTime(0, audioContext.currentTime); // Início silencioso
                gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.01); // Attack rápido
                gainNode.gain.exponentialRampToValueAtTime(0.15, audioContext.currentTime + 0.08); // Decay
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25); // Fade out

                // Toca o som por 250ms
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.25);

                console.log('✅ Som de notificação iniciado com sucesso');

                // Limpa após tocar
                oscillator.onended = () => {
                    console.log('🔚 Som de notificação finalizado');
                    oscillator.disconnect();
                    gainNode.disconnect();
                };

            } catch (error) {
                console.error('❌ Erro ao reproduzir som de notificação:', error);
                console.error('Detalhes do erro:', error.message, error.stack);
            }
        },

        /**
         * Adiciona uma nova notificação recebida via WebSocket
         * @param {Object} notification - Objeto da notificação
         */
        addNotification(notification) {
            console.log('🔔 Nova notificação recebida via WebSocket:', notification);
            console.log('🔍 Verificando se deve tocar som - read_at:', notification.read_at);
            
            const isUnread = !notification.read_at;
            
            // Toca som apenas se a notificação não foi lida
            if (isUnread) {
                console.log('🔊 Notificação não lida detectada. Tocando som...');
                this.playNotificationSound();
            } else {
                console.log('🔇 Notificação já foi lida. Som não será tocado.');
            }
            
            // Adiciona a notificação no início da lista (mais recente primeiro)
            this.notifications.unshift(notification);
            
            // Incrementa o contador de não lidas se a notificação não foi lida
            if (isUnread) {
                this.unreadCount++;
                console.log('📊 Contagem de não lidas atualizada:', this.unreadCount);
                
                // Dispara evento customizado para o componente de toast detectar
                // Isso é mais eficiente que usar watch no componente
                window.dispatchEvent(new CustomEvent('new-notification-received', {
                    detail: notification
                }));
            }
            
            // Limita a lista a um número razoável de notificações (ex: 50)
            if (this.notifications.length > 50) {
                this.notifications = this.notifications.slice(0, 50);
            }
        },

        /**
         * Inicia o listener de notificações em tempo real via WebSocket
         * @param {string} userId - ID do usuário autenticado
         */
        startListening(userId) {
            if (this.isListening) {
                console.log('⚠️ Listener de notificações já está ativo');
                return;
            }

            if (!window.Echo) {
                console.error('❌ Echo não está disponível. Verifique se o Echo foi inicializado.');
                return;
            }

            if (!userId) {
                console.error('❌ User ID não fornecido para iniciar o listener');
                return;
            }

            console.log('🎧 Iniciando listener de notificações em tempo real para usuário:', userId);
            
            try {
                const channelName = `user.${userId}`;
                // Conecta ao canal privado do usuário
                this.echoChannel = window.Echo.private(channelName);
                this.echoChannelName = channelName;
                
                // Escuta eventos de notificação
                // O backend envia o evento como .notification.created
                this.echoChannel
                    .listen('.notification.created', (notification) => {
                        console.log('🔔 Evento de notificação recebido (.notification.created):', notification);
                        this.handleNotificationEvent(notification);
                    })
                    .error((error) => {
                        console.error('❌ Erro no canal de notificações:', error);
                        // Se houver erro no WebSocket, reinicia o polling como fallback
                        console.warn('⚠️ Erro no WebSocket detectado. Reiniciando polling como fallback...');
                        this.reiniciarPollingComoFallback();
                    });

                this.isListening = true;
                console.log('✅ Listener de notificações iniciado com sucesso no canal:', channelName);
                console.log('📡 Aguardando notificações em tempo real...');
                
                // Se o polling estiver ativo, para ele já que o WebSocket está funcionando
                if (this.isPolling) {
                    console.log('🔄 WebSocket ativo detectado. Parando polling para economizar recursos...');
                    this.stopPolling();
                    console.log('✅ Polling parado. WebSocket agora é a única fonte de atualizações em tempo real.');
                }
            } catch (error) {
                console.error('❌ Erro ao iniciar listener de notificações:', error);
                console.warn('⚠️ O listener de notificações não pôde ser iniciado. Continuando com polling apenas.');
                this.isListening = false;
            }
        },

        /**
         * Reinicia o polling como fallback quando o WebSocket falha
         */
        reiniciarPollingComoFallback() {
            if (!this.isPolling) {
                console.log('🔄 Reiniciando polling como fallback devido a falha no WebSocket...');
                this.startPolling(30000);
            }
        },

        /**
         * Processa um evento de notificação recebido via WebSocket
         * @param {Object} notificationData - Dados da notificação
         */
        handleNotificationEvent(notificationData) {
            console.log('🔍 handleNotificationEvent - dados recebidos:', notificationData);
            
            // Normaliza o formato da notificação
            // O backend pode enviar title/message/action_url no nível superior ou em data
            const title = notificationData.title || notificationData.data?.title;
            const message = notificationData.message || notificationData.data?.message;
            const action_url = notificationData.action_url || notificationData.data?.action_url;
            
            const notification = {
                id: notificationData.id || notificationData.uuid,
                type: notificationData.type,
                // Preserva title, message e action_url no nível superior também
                // para facilitar o acesso no componente de toast
                title: title,
                message: message,
                action_url: action_url,
                data: {
                    title: title,
                    message: message,
                    action_url: action_url,
                    ...(notificationData.data || {}),
                    // Preserva campos adicionais que possam estar em data
                },
                read_at: notificationData.read_at || null,
                created_at: notificationData.created_at || new Date().toISOString()
            };

            console.log('✅ Notificação normalizada:', notification);

            // Adiciona a notificação
            this.addNotification(notification);
        },

        /**
         * Para o listener de notificações em tempo real
         */
        stopListening() {
            if (this.echoChannel && this.echoChannelName) {
                console.log('🛑 Parando listener de notificações no canal:', this.echoChannelName);
                try {
                    // Remove todos os listeners do canal
                    this.echoChannel.stopListening('.notification.created');
                    // Sai do canal usando leaveChannel
                    window.Echo.leaveChannel(`private-${this.echoChannelName}`);
                    
                    this.echoChannel = null;
                    this.echoChannelName = null;
                    this.isListening = false;
                    console.log('✅ Listener de notificações parado com sucesso');
                    
                    // Se o polling não estiver ativo, reinicia como fallback
                    if (!this.isPolling) {
                        console.log('🔄 WebSocket parado. Reiniciando polling como fallback...');
                        this.startPolling(30000);
                    }
                } catch (error) {
                    console.error('❌ Erro ao parar listener de notificações:', error);
                    // Tenta limpar mesmo com erro
                    this.echoChannel = null;
                    this.echoChannelName = null;
                    this.isListening = false;
                    
                    // Se o polling não estiver ativo, reinicia como fallback
                    if (!this.isPolling) {
                        console.log('🔄 WebSocket parado (com erro). Reiniciando polling como fallback...');
                        this.startPolling(30000);
                    }
                }
            } else {
                console.log('ℹ️ Nenhum listener ativo para parar');
            }
        },

        /**
         * Limpa o estado do store
         */
        clear() {
            this.notifications = [];
            this.unreadCount = 0;
            this.error = null;
            this.stopPolling();
            this.stopListening();
            
            // Fecha o contexto de áudio se existir
            if (this._audioContext && this._audioContext.state !== 'closed') {
                this._audioContext.close().catch(() => {
                    // Ignora erros ao fechar
                });
                this._audioContext = null;
            }
        }
    }
});

